import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus } from "lucide-react";

export default function AdminViolationsPage() {
  const { propertyId } = useProperty();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ unit: "", type: "", severity: "warning", description: "", fine: 0, resident_name: "" });

  const load = async () => {
    const { data } = await api.get(`/violations?property_id=${propertyId}`);
    setItems(data.items);
  };
  useEffect(() => { if (propertyId) load(); }, [propertyId]);

  const submit = async () => {
    await api.post("/violations", { ...form, fine: Number(form.fine) || 0, property_id: propertyId });
    toast.success("Violation issued");
    setOpen(false); setForm({ unit: "", type: "", severity: "warning", description: "", fine: 0, resident_name: "" });
    load();
  };

  const resolve = async (id) => {
    await api.patch(`/violations/${id}/resolve`);
    toast.success("Resolved");
    load();
  };

  return (
    <div>
      <PageHeader
        eyebrow="Compliance"
        title="Violations"
        subtitle="Issue and track HOA violations."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="issue-violation-btn" className="bg-orange-500 hover:bg-orange-600 rounded-full">
                <Plus className="h-4 w-4 mr-1" /> Issue violation
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-white/10">
              <DialogHeader><DialogTitle>Issue Violation</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Unit</Label><Input data-testid="v-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} /></div>
                <div><Label>Resident name</Label><Input data-testid="v-name" value={form.resident_name} onChange={(e) => setForm({ ...form, resident_name: e.target.value })} /></div>
                <div><Label>Type</Label><Input data-testid="v-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} placeholder="e.g. Noise complaint" /></div>
                <div>
                  <Label>Severity</Label>
                  <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="warning">Warning</SelectItem>
                      <SelectItem value="fine">Fine</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Fine (USD)</Label><Input data-testid="v-fine" type="number" value={form.fine} onChange={(e) => setForm({ ...form, fine: e.target.value })} /></div>
                <div><Label>Description</Label><Textarea data-testid="v-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                <Button data-testid="v-submit" onClick={submit} className="w-full bg-orange-500 hover:bg-orange-600">Issue</Button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />
      <Card className="border-white/10 bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10">
              <TableHead>Unit</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Severity</TableHead>
              <TableHead className="text-right">Fine</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((v) => (
              <TableRow key={v.id} className="border-white/5">
                <TableCell>{v.unit}</TableCell>
                <TableCell>
                  <div className="font-medium">{v.type}</div>
                  <div className="text-xs text-zinc-500 truncate max-w-md">{v.description}</div>
                </TableCell>
                <TableCell><Badge variant="outline" className="border-white/10 capitalize">{v.severity}</Badge></TableCell>
                <TableCell className="text-right tabular-nums">${Number(v.fine || 0).toLocaleString()}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={v.status === "open" ? "border-orange-500/40 text-orange-300 bg-orange-500/10" : "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"}>
                    {v.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  {v.status === "open" && <Button size="sm" variant="outline" onClick={() => resolve(v.id)} data-testid={`v-resolve-${v.id}`} className="border-white/10">Resolve</Button>}
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground p-8">No violations.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
